import { useTranslation } from "react-i18next";
import { Checkbox } from "~/atoms/checkbox";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { type Option } from "~/molecules/ControlledCombobox";
import {
  DraftTableRow,
  type DraftRowHoverCol,
} from "~/molecules/onboard-documents/DraftTableRow";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { type DocumentVersionSummary } from "~/api/types";
import { findDuplicateDocumentVersion } from "~/helpers/document";
import { type DraftRow, type RowPatch } from "~/helpers/draftRows";

interface DraftsTableProps {
  rows: DraftRow[];
  selectedIds: Set<string>;
  allSelected: boolean;
  isCreating: boolean;
  tierOptions: Option[] | undefined;
  categoryOptions: Option[] | undefined;
  documentVersions: DocumentVersionSummary[] | undefined;
  createdCount: number;
  onToggleAll: () => void;
  onToggleRow: (id: string) => void;
  onUpdateRow: (id: string, patch: RowPatch, opts?: { bulk?: boolean }) => void;
  onCreateRow: (row: DraftRow) => void;
  onRemoveRow: (id: string) => void;
}

export const DraftsTable = ({
  rows,
  selectedIds,
  allSelected,
  isCreating,
  tierOptions,
  categoryOptions,
  documentVersions,
  createdCount,
  onToggleAll,
  onToggleRow,
  onUpdateRow,
  onCreateRow,
  onRemoveRow,
}: DraftsTableProps) => {
  const { t } = useTranslation();
  const { onHover, onHoverLeave, isHighlighted } = useBulkHoverHighlight<
    string,
    DraftRowHoverCol
  >(selectedIds);

  return (
    <Table wrapperClassName="rounded-md border">
      <TableHeader>
        <TableRow>
          <TableHead>
            <Checkbox
              checked={allSelected}
              onCheckedChange={onToggleAll}
              aria-label={t(($) => $.commons.buttons.selectAll)}
            />
          </TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.createDrafts.columns.title,
            )}
          </TableHead>
          <TableHead>
            {t(
              ($) => $.library.onboardDocuments.steps.createDrafts.columns.tier,
            )}
          </TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.createDrafts.columns.category,
            )}
          </TableHead>
          <TableHead className="text-right">
            {t(
              ($) => $.library.onboardDocuments.steps.createDrafts.createdLabel,
              { createdCount, totalCount: rows.length },
            )}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const hasDuplicate = !!findDuplicateDocumentVersion({
            documentVersions,
            title: row.title,
            categoryId: row.categoryId === null ? null : Number(row.categoryId),
          });

          return (
            <DraftTableRow
              key={row.id}
              row={row}
              isSelected={selectedIds.has(row.id)}
              isCreating={isCreating}
              hasDuplicate={hasDuplicate}
              tierOptions={tierOptions}
              categoryOptions={categoryOptions}
              onToggleSelection={() => onToggleRow(row.id)}
              onUpdate={(patch, opts) => onUpdateRow(row.id, patch, opts)}
              onCreate={() => onCreateRow(row)}
              onRemove={() => onRemoveRow(row.id)}
              onHoverCell={(col) => onHover(row.id, col)}
              onHoverLeave={onHoverLeave}
              isBulkHighlighted={(col) => isHighlighted(row.id, col)}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};
